import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Download, Share, PlusSquare, X, MoreVertical } from "lucide-react";

// O navegador avisa uma única vez que o app pode ser instalado: guardamos o aviso aqui.
let deferred = null;
const listeners = new Set();
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => { e.preventDefault(); deferred = e; listeners.forEach((f) => f()); });
  window.addEventListener("appinstalled", () => { deferred = null; try { localStorage.setItem("iep-app-instalado", "1"); } catch (e) {} listeners.forEach((f) => f()); });
}

export const isStandalone = () =>
  typeof window !== "undefined" && (window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true);
/** app já instalado neste aparelho (aberto como app agora ou instalado antes por aqui) */
export const appInstalado = () => {
  if (isStandalone()) { try { localStorage.setItem("iep-app-instalado", "1"); } catch (e) {} return true; }
  try { return localStorage.getItem("iep-app-instalado") === "1"; } catch (e) { return false; }
};
/** Avisos que somem sozinhos: some após `segundos` e deixa de aparecer depois de `maxVezes` exibições neste aparelho. */
export function useAvisoTemporario(chave, { segundos = 20, maxVezes = 3, ativo = true } = {}) {
  const [visivel, setVisivel] = useState(() => { try { return Number(localStorage.getItem(chave + "-vezes") || 0) < maxVezes; } catch (e) { return true; } });
  const [contou, setContou] = useState(false);
  useEffect(() => {
    if (!visivel || !ativo) return undefined;      // só conta e cronometra quando o aviso está mesmo na tela
    if (!contou) { setContou(true); try { localStorage.setItem(chave + "-vezes", String(Number(localStorage.getItem(chave + "-vezes") || 0) + 1)); } catch (e) {} }
    const t = setTimeout(() => setVisivel(false), segundos * 1000);
    return () => clearTimeout(t);
  }, [visivel, ativo]); // eslint-disable-line react-hooks/exhaustive-deps
  const nuncaMais = () => { setVisivel(false); try { localStorage.setItem(chave + "-vezes", String(maxVezes)); } catch (e) {} };
  return [visivel, () => setVisivel(false), nuncaMais];
}
const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
const isMobile = () => /android|iphone|ipad|ipod|mobile/i.test(navigator.userAgent);

export function useInstall() {
  const [, force] = useState(0);
  useEffect(() => { const f = () => force((n) => n + 1); listeners.add(f); return () => listeners.delete(f); }, []);
  return { canPrompt: !!deferred, installed: isStandalone(), ios: isIOS() };
}

/** Janela com o botão "Instalar agora" (Android/PC) ou o passo a passo (iPhone). */
/** `app` troca nome, ícone e textos (ex.: o app Tricolab); sem ele é o app da IEP. */
const APP_IEP = { nome: "IEP", icone: "/brand/app-192.png", desc: "Acesse seus cursos com um toque, em tela cheia." };
export function InstallDialog({ open, onOpenChange, app = APP_IEP }) {
  const { canPrompt, ios, installed } = useInstall();
  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    await deferred.userChoice.catch(() => {});
    deferred = null; listeners.forEach((f) => f());
    onOpenChange(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <img src={app.icone} alt="" className="h-12 w-12 rounded-xl border border-slate-200" />
            <div><DialogTitle className="font-display">Instalar o app {app.nome}</DialogTitle>
              <DialogDescription>{app.desc}</DialogDescription></div>
          </div>
        </DialogHeader>
        {installed ? (
          <p className="text-sm text-green-700">O app já está instalado neste aparelho. 🎉</p>
        ) : canPrompt ? (
          <Button onClick={install} className="w-full text-white h-11" style={{ background: "#1E9E2A" }} data-testid="install-now"><Download className="h-4 w-4 mr-2" /> Instalar agora</Button>
        ) : ios ? (
          <ol className="space-y-3 text-sm text-slate-700" data-testid="install-ios-steps">
            <li className="flex gap-3"><span className="font-bold text-[#1E9E2A]">1.</span><span>Abra este site no <strong>Safari</strong>.</span></li>
            <li className="flex gap-3"><span className="font-bold text-[#1E9E2A]">2.</span><span>Toque em <Share className="inline h-4 w-4 -mt-1" /> <strong>Compartilhar</strong> (na barra de baixo).</span></li>
            <li className="flex gap-3"><span className="font-bold text-[#1E9E2A]">3.</span><span>Escolha <PlusSquare className="inline h-4 w-4 -mt-1" /> <strong>Adicionar à Tela de Início</strong> e depois <strong>Adicionar</strong>.</span></li>
          </ol>
        ) : isMobile() ? (
          <ol className="space-y-3 text-sm text-slate-700">
            <li className="flex gap-3"><span className="font-bold text-[#1E9E2A]">1.</span><span>Abra este site no <strong>Chrome</strong> (ou no navegador Samsung).</span></li>
            <li className="flex gap-3"><span className="font-bold text-[#1E9E2A]">2.</span><span>Toque no menu <MoreVertical className="inline h-4 w-4 -mt-1" /> (três pontinhos) ou ☰ e escolha <strong>Instalar app</strong> ou <strong>Adicionar à tela inicial</strong>.</span></li>
            <li className="flex gap-3"><span className="font-bold text-[#1E9E2A]">3.</span><span>Confirme. O ícone {app.de || (app.nome === "IEP" ? "da IEP" : `do ${app.nome}`)} aparece junto dos seus outros apps.</span></li>
          </ol>
        ) : (
          <ol className="space-y-3 text-sm text-slate-700">
            <li className="flex gap-3"><span className="font-bold text-[#1E9E2A]">1.</span><span>Use o <strong>Google Chrome</strong> ou o <strong>Microsoft Edge</strong>.</span></li>
            <li className="flex gap-3"><span className="font-bold text-[#1E9E2A]">2.</span><span>Clique no ícone de instalar na barra de endereço (computador com seta) ou no menu <MoreVertical className="inline h-4 w-4 -mt-1" /> → <strong>Instalar {app.nome}</strong> / <strong>Aplicativos → Instalar este site</strong>.</span></li>
            <li className="flex gap-3"><span className="font-bold text-[#1E9E2A]">3.</span><span>Pronto: {app.abre || (app.nome === "IEP" ? "a IEP abre" : `o ${app.nome} abre`)} em janela própria e ganha atalho na área de trabalho.</span></li>
          </ol>
        )}
      </DialogContent>
    </Dialog>
  );
}

const CHAVE = "iep-install-adiado-ate";

/** Faixa "Instale o app IEP" para aluna e professora: aparece UMA vez por aparelho (some sozinha em 20 s) e o X
 * esconde para sempre. Quem quiser instalar depois usa "Instalar app" no menu ou no login. */
export function InstallBanner({ depois = false }) {
  const { installed, canPrompt } = useInstall();
  const [open, setOpen] = useState(false);
  const [hidden, setHidden] = useState(() => { try { return Number(localStorage.getItem(CHAVE) || 0) > Date.now(); } catch (e) { return false; } });
  const ativo = !(installed || appInstalado() || hidden || depois || (!isMobile() && !canPrompt));
  const [visivel, , nuncaMais] = useAvisoTemporario("iep-aviso-instalar", { segundos: 20, maxVezes: 1, ativo });
  if (!ativo || !visivel) return null;
  const close = () => { setHidden(true); nuncaMais(); };
  const instalar = async () => {
    if (deferred) {       // Android/Chrome/Edge: abre direto a janela de instalação do navegador
      deferred.prompt();
      await deferred.userChoice.catch(() => {});
      deferred = null; listeners.forEach((f) => f());
      return;
    }
    setOpen(true);        // iPhone e navegadores sem instalação direta: passo a passo
  };
  return (
    <>
      <div className="mx-4 mt-3 flex items-center gap-3 rounded-xl border border-green-200 bg-green-50 px-3 py-2.5" data-testid="install-banner">
        <img src="/brand/app-192.png" alt="" className="h-9 w-9 rounded-lg" />
        <div className="flex-1 text-sm text-slate-700"><strong>Instale o app IEP</strong><div className="text-xs text-slate-500">{isMobile() ? "Seus cursos na tela inicial do celular, com um toque." : "Abra seus cursos em janela própria, direto da área de trabalho."}</div></div>
        <Button size="sm" onClick={instalar} className="text-white" style={{ background: "#1E9E2A" }} data-testid="install-banner-btn"><Download className="h-4 w-4 mr-1" /> Instalar</Button>
        <button onClick={close} className="text-slate-400 hover:text-slate-600" aria-label="Fechar aviso" title="Fechar aviso"><X className="h-4 w-4" /></button>
      </div>
      <InstallDialog open={open} onOpenChange={setOpen} />
    </>
  );
}
